/**
 * Sticky review-tabs scrollspy.
 *
 * Markup contract (see src/blocks/review-tabs/render.php):
 *   <nav data-review-tabs>
 *     <ul.cwp-review-tabs__list>
 *       <li><a.cwp-review-tabs__link data-tab-anchor="bonus">Welcome bonus</a></li>
 *       ...
 *
 * Each `data-tab-anchor` should match a heading id somewhere on the page.
 */
export const cwpReviewTabs = {
	init() {
		document.querySelectorAll('[data-review-tabs]').forEach((nav) => {
			this._park(nav);
			this._bind(nav);
		});
	},

	/**
	 * The tabs end the review head's group, which is the sticky box
	 * (frontend.scss, "Review tabs"): measure what its top parks above the
	 * window, everything above the tabs. Again when the logo or a font loads.
	 */
	_park(nav) {
		const head = nav.parentElement;
		new ResizeObserver(() => {
			head.style.setProperty('--cwp-review-head-hide', `${nav.getBoundingClientRect().top - head.getBoundingClientRect().top}px`);
		}).observe(head);
	},

	_bind(nav) {
		const links = Array.from(nav.querySelectorAll('[data-tab-anchor]'));

		if (links.length === 0) return;

		const targets = links
			.map((link) => {
				const id = link.dataset.tabAnchor;
				const el = id ? document.getElementById(id) : null;
				return el ? { link, el } : null;
			})
			.filter(Boolean);

		if (targets.length === 0) return;

		// Smooth-scroll with offset for the sticky header + tabs.
		links.forEach((link) => {
			link.addEventListener('click', (event) => {
				const id = link.dataset.tabAnchor;
				const target = document.getElementById(id);
				if (!target) return;
				event.preventDefault();
				// The tabs stick within the review's content: past its end (the
				// verdict box) only what is stuck above them stays.
				const box = nav.closest('.wp-block-post-content');
				const offset = box && !box.contains(target) ? parseFloat(getComputedStyle(nav).top) || 0 : this._offset(nav);
				const top =
					target.getBoundingClientRect().top + window.scrollY - offset - 8;
				window.scrollTo({ top, behavior: 'smooth' });
				history.replaceState(null, '', `#${id}`);
			});
		});

		// Scrollspy via IntersectionObserver.
		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (!entry.isIntersecting) return;
					const id = entry.target.id;
					links.forEach((link) => {
						const active = link.dataset.tabAnchor === id;
						link.classList.toggle('is-active', active);
						if (active) this._reveal(link);
					});
				});
			},
			{
				rootMargin: `-${this._offset(nav) + 16}px 0px -55% 0px`,
				threshold: 0,
			}
		);

		targets.forEach(({ el }) => observer.observe(el));
	},

	/**
	 * Keep the active tab in sight in its strip, which scrolls sideways when
	 * the tabs do not fit (phones): stuck, the strip stays on screen, and its
	 * highlight had scrolled out of it. Scrolls the strip only, not the page.
	 */
	_reveal(link) {
		const list = link.closest('.cwp-review-tabs__list');
		if (!list) return;
		const l = list.getBoundingClientRect();
		const r = link.getBoundingClientRect();
		if (r.left >= l.left && r.right <= l.right) return;
		list.scrollLeft += r.left - l.left - (l.width - r.width) / 2;
	},

	_offset(nav) {
		// What covers a heading once the tabs stick: their own top (what is
		// stuck above them: the desktop menu row, the admin bar) and themselves.
		return (parseFloat(getComputedStyle(nav).top) || 0) + nav.getBoundingClientRect().height;
	},
};
